/**
 * Defence 3, after it has fired: why Wend stopped this campaign, and the one
 * action that starts it again.
 *
 * ## The sentence is the product here, and it is not written in this file
 *
 * A halt is the only state in Reach where the screen has to tell a
 * non-technical person to go and do something about their **own** Amazon
 * account. So the sentence comes from `CAMPAIGN_HALT_SENTENCES`, keyed on
 * `campaigns.halt_reason`, and the API's own `halt.message` — which carries the
 * counts at the instant it stopped — is preferred over the standing one. The
 * standing sentence is the fallback for a row whose message never made it, so
 * the panel fails to an explanation rather than to a bare status.
 *
 * The reason is **per channel and the status is not**, deliberately. The badge
 * above says "Stopped — needs a look" on either channel because
 * `CAMPAIGN_STATUS_DISPLAY` is keyed on status alone — slice 17's recorded wart,
 * where `sending` says "Amazon SES" on a WhatsApp campaign. Baking the channel
 * into the status would have reproduced that fault knowingly. This panel is
 * where the channel-specific half lives, and it never composes it: an email halt
 * talks about bounces and a reviewed AWS account, a WhatsApp halt talks about
 * unreachable numbers and a quality rating, and both sentences were written
 * whole in `@wend/shared`.
 *
 * Two of the four reasons are the fail-closed branch — `risk_unmeasurable` and
 * `plan_unreadable` — and they get the same treatment rather than being hidden
 * as internal errors. A campaign that stopped because Wend could not read its
 * own plan has still stopped, several hundred people are still waiting, and the
 * person who has to decide what happens next is the one on this screen.
 *
 * ## Why the acknowledgement is a checkbox and not a constant
 *
 * `resumeHaltedCampaignRequestSchema` types `acknowledged` as `z.literal(true)`:
 * the contract **cannot express** a resume that did not acknowledge the halt.
 * The tempting implementation is to send `true` from the click handler, which
 * satisfies the type and makes the literal a decoration — the client would be
 * asserting something about the person that the person never did.
 *
 * So the button is gated on a checkbox, and the checkbox is what the literal
 * describes. It also gives the two-step shape every other irreversible action on
 * this screen has: cancelling confirms inline, scheduling confirms inline, and
 * resuming a campaign that stopped because a list is failing is the same class
 * of decision — the next batch goes to real people through somebody's own
 * provider account.
 *
 * **The checkbox's own two sentences are the contract's, and the second one is
 * per channel.** They were written into this file, and the stake read *"The next
 * batch goes to real addresses through your own sending account"* — on a panel
 * that is drawn whenever `CampaignListSafety.halt` is set, which is on either
 * channel. A WhatsApp tenant was asked to accept a consequence stated in email's
 * nouns at the one moment on this screen where they are agreeing to something.
 * `RESUME_ACKNOWLEDGEMENT_LABEL` and `resumeAcknowledgementStake(channel)` are
 * exhaustive over `Channel` in `@wend/shared`, so a third channel is a compile
 * error rather than a sentence that quietly stays email's.
 *
 * **Resuming is not the only way out and the panel says so by being beside the
 * cancel card rather than replacing it.** `isCampaignCancellable` includes
 * `halted_delivery_risk` precisely so that a halt cannot trap several hundred
 * recipients in `pending` for ever; the campaign screen keeps rendering the
 * cancel card underneath this one on exactly that argument.
 */
import {
  CAMPAIGN_HALT_SENTENCES,
  RESUME_ACKNOWLEDGEMENT_LABEL,
  resumeAcknowledgementStake,
  type Campaign,
  type CampaignHaltReason,
} from '@wend/shared';
import { Button, Callout, Checkbox, useToast } from '@wend/ui';
import { Play } from 'lucide-react';
import { useState } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { resumeHaltedCampaign } from '../../lib/api/campaigns';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { formatDateTime } from '../../lib/format';

export interface CampaignHaltPanelProps {
  campaign: Campaign;
  halt: {
    readonly reason: CampaignHaltReason;
    readonly message: string;
    readonly haltedAt: string;
  };
  /** The campaign as the resume left it — status, counts and all. */
  onResumed: (campaign: Campaign) => void;
}

export function CampaignHaltPanel({ campaign, halt, onResumed }: CampaignHaltPanelProps) {
  const { toast } = useToast();
  const [acknowledged, setAcknowledged] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );

  // The message the halt was recorded with carries its counts; the standing
  // sentence is what stands in when the row has none.
  const sentence = halt.message.trim() === '' ? CAMPAIGN_HALT_SENTENCES[halt.reason] : halt.message;

  async function onResume(): Promise<void> {
    if (pending || !acknowledged) return;

    setFailure(null);
    setPending(true);

    try {
      const next = await resumeHaltedCampaign(campaign.id);
      toast({
        tone: 'success',
        title: 'Campaign resumed',
        description: 'Sending continues from where it stopped. Nobody is sent to twice.',
      });
      onResumed(next);
      setPending(false);
      setAcknowledged(false);
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
      setPending(false);
    }
  }

  return (
    <section className="flex flex-col gap-4" aria-label="Why this campaign stopped">
      {failure !== null && (
        <FormAlert
          tone="error"
          {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
        >
          {failure.message}
        </FormAlert>
      )}

      <Callout tone="warn" title="Wend stopped this campaign">
        {sentence}
      </Callout>

      <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
        stopped {formatDateTime(halt.haltedAt)}
      </p>

      <div className="flex flex-col gap-3 rounded-base border border-line-strong bg-white/[0.02] px-4 py-4">
        <Checkbox
          checked={acknowledged}
          onCheckedChange={setAcknowledged}
          disabled={pending}
          label={RESUME_ACKNOWLEDGEMENT_LABEL}
          // Per channel: this panel is drawn on either, and the superseded
          // literal said "real addresses through your own sending account" on a
          // campaign going through Meta. See the constant's header.
          description={resumeAcknowledgementStake(campaign.channel)}
        />

        <div>
          <Button
            type="button"
            disabled={!acknowledged}
            loading={pending}
            onClick={() => {
              void onResume();
            }}
          >
            <Play aria-hidden="true" className="size-4" />
            {pending ? 'Resuming' : 'Resume sending'}
          </Button>
        </div>
      </div>
    </section>
  );
}
